
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Chip,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    Card,
    CardContent,
    Avatar,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon
} from '@mui/material';
import {
    ShoppingCart as ShoppingCartIcon,
    Warning as WarningIcon,
    Build as BuildIcon,
    Inventory as InventoryIcon,
    LocalShipping as ShippingIcon,
    ArrowForward as ArrowForwardIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import axios from 'axios';
import { machineLabel } from '../../utils/machineLabel';

const ProcurementPage = () => {
    const [data, setData] = useState({
        lowStock: [],
        serviceRequests: [],
        recentPOs: []
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const res = await axios.get('/api/procurement/dashboard');
            if (res.data.success) {
                setData(res.data.data);
            }
        } catch (err) {
            console.error("Failed to fetch procurement data", err);
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ title, count, icon, color, subtitle }) => (
        <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid rgba(226, 232, 240, 0.8)', boxShadow: '0 4px 6px rgba(0,0,0,0.02)', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: -10, right: -10, opacity: 0.1, transform: 'rotate(15deg)' }}>
                {React.cloneElement(icon, { sx: { fontSize: 100, color: color } })}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Avatar sx={{ bgcolor: `${color}15`, color: color, borderRadius: '12px' }}>
                    {icon}
                </Avatar>
                <Typography variant="h6" fontWeight={700} color="text.primary">{title}</Typography>
            </Box>
            <Typography variant="h3" fontWeight={800} sx={{ my: 1 }}>{count}</Typography>
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
        </Paper>
    );

    return (
        <Box sx={{ pb: 5 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Button
                    variant="contained"
                    startIcon={<ShoppingCartIcon />}
                    sx={{
                        bgcolor: '#0f766e',
                        '&:hover': { bgcolor: '#115e59' },
                        borderRadius: '12px',
                        px: 3, py: 1
                    }}
                >
                    Create Purchase Order
                </Button>
            </Box>

            {/* Stats Row */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                    <StatCard
                        title="Low Stock Items"
                        count={data.lowStock.length}
                        icon={<WarningIcon />}
                        color="#ef4444"
                        subtitle="Items below reorder level (Stores)"
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <StatCard
                        title="Service Requests"
                        count={data.serviceRequests.length}
                        icon={<BuildIcon />}
                        color="#f59e0b"
                        subtitle="Vehicles waiting for parts"
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <StatCard
                        title="Active POs"
                        count={data.recentPOs.filter(p => p.status !== 'RECEIVED' && p.status !== 'CANCELLED').length}
                        icon={<ShippingIcon />}
                        color="#3b82f6"
                        subtitle="Orders in transit"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Left Col: Needs Attention */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                        {/* Store Needs (Low Stock) */}
                        <Paper sx={{ borderRadius: '16px', border: '1px solid rgba(226, 232, 240, 0.8)', overflow: 'hidden' }}>
                            <Box sx={{ p: 2.5, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#fef2f2' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <InventoryIcon sx={{ color: '#ef4444' }} />
                                    <Typography variant="h6" fontWeight={700} color="#991b1b">
                                        Store Replenishment Needed
                                    </Typography>
                                </Box>
                                <Chip label={`${data.lowStock.length} Items`} size="small" color="error" sx={{ fontWeight: 700 }} />
                            </Box>
                            <TableContainer sx={{ maxHeight: 300 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Item Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Current Stock</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Reorder Level</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.lowStock.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                                    No low stock items. Inventory is healthy!
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            data.lowStock.map((item) => (
                                                <TableRow key={item.id} hover>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">#{item.item_code}</Typography>
                                                    </TableCell>
                                                    <TableCell>{item.category}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={`${item.current_stock} ${item.unit || ''}`}
                                                            size="small"
                                                            sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 700 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">{item.reorder_level}</TableCell>
                                                    <TableCell align="right">
                                                        <Button size="small" variant="outlined" color="error" sx={{ borderRadius: '8px', textTransform: 'none' }}>
                                                            Order
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                        {/* Vehicle Needs (Service Tickets) */}
                        <Paper sx={{ borderRadius: '16px', border: '1px solid rgba(226, 232, 240, 0.8)', overflow: 'hidden' }}>
                            <Box sx={{ p: 2.5, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#fffbeb' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <BuildIcon sx={{ color: '#d97706' }} />
                                    <Typography variant="h6" fontWeight={700} color="#92400e">
                                        Vehicle Parts Requests
                                    </Typography>
                                </Box>
                                <Chip label={`${data.serviceRequests.length} Requests`} size="small" sx={{ bgcolor: '#fcd34d', color: '#78350f', fontWeight: 700 }} />
                            </Box>
                            <TableContainer sx={{ maxHeight: 300 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Vehicle / Equipment</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Requested By</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.serviceRequests.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                                    No vehicles waiting for parts.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            data.serviceRequests.map((req) => (
                                                <TableRow key={req.ticket_id} hover>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={600}>{machineLabel(req)}</Typography>
                                                        <Typography variant="caption" color="text.secondary">Ticket #{req.ticket_id}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={req.priority}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: req.priority === 'HIGH' || req.priority === 'CRITICAL' ? '#fee2e2' : '#fef3c7',
                                                                color: req.priority === 'HIGH' || req.priority === 'CRITICAL' ? '#b91c1c' : '#92400e',
                                                                fontWeight: 700,
                                                                fontSize: '0.7rem'
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{req.description}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Avatar sx={{ width: 24, height: 24, fontSize: 10, bgcolor: 'primary.main', display: 'inline-flex', mr: 1 }}>
                                                            {req.requested_by?.charAt(0) || '?'}
                                                        </Avatar>
                                                        {req.requested_by}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Button size="small" variant="contained" color="warning" sx={{ borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }}>
                                                            Resolve
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                    </Box>
                </Grid>

                {/* Right Col: Recent Activity / Quick Actions */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ borderRadius: '16px', border: '1px solid rgba(226, 232, 240, 0.8)', overflow: 'hidden', height: '100%' }}>
                        <Box sx={{ p: 2.5, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography variant="h6" fontWeight={700}>Recent Activity</Typography>
                        </Box>
                        <List>
                            {data.recentPOs.map((po) => (
                                <React.Fragment key={po.id}>
                                    <ListItem>
                                        <ListItemIcon>
                                            <CheckCircleIcon color="success" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2" fontWeight={600}>
                                                    PO #{po.po_number} - {po.status}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography variant="caption" color="text.secondary">
                                                    {po.supplier_name} • {new Date(po.created_at).toLocaleDateString()}
                                                </Typography>
                                            }
                                        />
                                        <ArrowForwardIcon fontSize="small" color="action" />
                                    </ListItem>
                                    <Divider variant="inset" component="li" />
                                </React.Fragment>
                            ))}
                        </List>
                        <Box sx={{ p: 2 }}>
                            <Button fullWidth variant="outlined" sx={{ borderRadius: '10px', textTransform: 'none' }}>
                                View All Purchase Orders
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ProcurementPage;
